import type { ReactNode } from "react";

interface PageHeaderProps {
	kicker: ReactNode;
	title: ReactNode;
	qualifier?: ReactNode;
	description?: ReactNode;
	actions?: ReactNode;
	className?: string;
}

export const Kicker = ({
	children,
	tone = "brand",
	className = "",
}: {
	children: ReactNode;
	tone?: "brand" | "muted";
	className?: string;
}) => (
	<p
		className={`font-semibold text-[0.7rem] uppercase tracking-[0.18em] ${
			tone === "brand" ? "text-brand-ink" : "text-zinc-500"
		} ${className}`}
	>
		{children}
	</p>
);

export const PageHeader = ({
	kicker,
	title,
	qualifier,
	description,
	actions,
	className = "",
}: PageHeaderProps) => {
	const side = description || actions;
	return (
		<header
			className={`grid gap-6 border-zinc-200 border-b pb-7 lg:grid-cols-12 ${className}`}
		>
			<div className={side ? "lg:col-span-7" : "lg:col-span-12"}>
				<Kicker>{kicker}</Kicker>
				<h1 className="mt-3 font-semibold text-[30px] text-ink leading-[1.12] tracking-[-0.01em]">
					{title}
					{qualifier && (
						<>
							{" "}
							<span className="text-zinc-400">{qualifier}</span>
						</>
					)}
				</h1>
			</div>
			{side && (
				<div className="flex flex-col justify-end gap-4 lg:col-span-5 lg:col-start-8">
					{description && (
						<div className="text-[13px] text-zinc-500 leading-relaxed">
							{description}
						</div>
					)}
					{actions && (
						<div className="flex flex-wrap items-center gap-3">{actions}</div>
					)}
				</div>
			)}
		</header>
	);
};

export const SectionHeading = ({
	kicker,
	title,
	qualifier,
	actions,
	className = "",
}: Omit<PageHeaderProps, "description">) => (
	<div
		className={`flex flex-wrap items-end justify-between gap-4 border-zinc-200 border-b pb-4 ${className}`}
	>
		<div className="min-w-0">
			<Kicker tone="muted">{kicker}</Kicker>
			<h2 className="mt-2 font-semibold text-[20px] text-ink leading-tight">
				{title}
				{qualifier && (
					<>
						{" "}
						<span className="text-zinc-400">{qualifier}</span>
					</>
				)}
			</h2>
		</div>
		{actions && <div className="flex items-center gap-3">{actions}</div>}
	</div>
);
